import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import IconButton from '@mui/material/IconButton';
import ListItemButton from '@mui/material/ListItemButton';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import {
  ChevronDownIcon,
  ChevronRightIcon,
  NestedFilesIcon,
} from '@tyro/icons';
import type { ReturnTypeFromUseDocumentationPages } from '../../api/pages';
import { formatDuration } from '../../utils/video-format';
import { PageKindIcon } from '../page-kind-icon';

const INDENT_PER_LEVEL = 3;

export interface TreeNode {
  page: ReturnTypeFromUseDocumentationPages;
  children: TreeNode[];
}

export function buildTree(
  pages: ReturnTypeFromUseDocumentationPages[],
): TreeNode[] {
  const map = new Map<number, TreeNode>();
  const roots: TreeNode[] = [];

  for (const page of pages) {
    map.set(page.id, { page, children: [] });
  }

  for (const page of pages) {
    const node = map.get(page.id);
    if (!node) continue;
    if (page.parentPageId && map.has(page.parentPageId)) {
      map.get(page.parentPageId)?.children.push(node);
    } else {
      roots.push(node);
    }
  }

  const sort = (nodes: TreeNode[]) => {
    nodes.sort((a, b) => a.page.sortOrder - b.page.sortOrder);
    for (const node of nodes) sort(node.children);
  };
  sort(roots);

  return roots;
}

interface ReaderPageItemProps {
  node: TreeNode;
  level: number;
  expanded: Set<number>;
  onToggle: (pageId: number) => void;
  onSelectPage: () => void;
}

export function ReaderPageItem({
  node,
  level,
  expanded,
  onToggle,
  onSelectPage,
}: ReaderPageItemProps) {
  const hasChildren = node.children.length > 0;
  const isExpanded = expanded.has(node.page.id);
  const isVideo = node.page.entityType === 'video';
  const durationSec = isVideo
    ? (node.page.videoData?.durationSec ?? null)
    : null;

  return (
    <>
      <ListItemButton
        onClick={onSelectPage}
        sx={{
          gap: 1.25,
          pl: 1.25 + level * INDENT_PER_LEVEL,
          pr: 2,
          py: 1.25,
        }}
      >
        {hasChildren ? (
          <IconButton
            size="small"
            onClick={(event) => {
              event.stopPropagation();
              onToggle(node.page.id);
            }}
            sx={{
              width: 24,
              height: 24,
              p: 0,
              color: 'inherit',
              flexShrink: 0,
            }}
          >
            {isExpanded ? (
              <ChevronDownIcon sx={{ fontSize: 16 }} />
            ) : (
              <ChevronRightIcon sx={{ fontSize: 16 }} />
            )}
          </IconButton>
        ) : (
          <Box sx={{ width: 24, height: 24, flexShrink: 0 }} />
        )}
        {hasChildren ? (
          <NestedFilesIcon
            sx={{ fontSize: 18, color: 'text.disabled', flexShrink: 0 }}
          />
        ) : (
          <PageKindIcon
            entityType={node.page.entityType}
            withTooltip
            sx={{ fontSize: 18, color: 'text.disabled', flexShrink: 0 }}
          />
        )}
        {hasChildren && isVideo && (
          <PageKindIcon
            entityType="video"
            withTooltip
            sx={{ fontSize: 16, color: 'text.disabled', flexShrink: 0 }}
          />
        )}
        <Typography
          variant="body2"
          noWrap
          sx={{
            flex: 1,
            fontWeight: hasChildren ? 600 : 500,
            color: 'inherit',
          }}
        >
          {node.page.title}
        </Typography>
        {durationSec != null && (
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{
              fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
              fontSize: 11,
              flexShrink: 0,
            }}
          >
            {formatDuration(durationSec)}
          </Typography>
        )}
        {hasChildren && (
          <Chip
            label={node.children.length}
            size="small"
            sx={{
              flexShrink: 0,
              height: 20,
              minWidth: 24,
              fontSize: 11,
              fontWeight: 600,
              bgcolor: 'slate.100',
              color: 'text.disabled',
            }}
          />
        )}
      </ListItemButton>
      {hasChildren && isExpanded && (
        <Stack>
          {node.children.map((child) => (
            <ReaderPageItem
              key={child.page.id}
              node={child}
              level={level + 1}
              expanded={expanded}
              onToggle={onToggle}
              onSelectPage={onSelectPage}
            />
          ))}
        </Stack>
      )}
    </>
  );
}
